import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import {
  useUsageTimeseries,
  useUsageLogsInfinite,
  useUsageFilters,
  useApiKeys,
} from "../hooks/useApi";
import Card from "../components/Card";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import TokenChart from "../components/TokenChart";
import { formatNumber, formatToman } from "../utils/format";
import {
  USAGE_DURATIONS as DURATIONS,
  USAGE_DURATION_SECONDS as DURATION_SECONDS,
  LOGS_DURATION_STORAGE_KEY as DURATION_STORAGE_KEY,
  loadLogsDuration as loadDuration,
} from "../utils/usage";

const STATUS_LABELS: Record<string, string> = {
  pending: "در انتظار",
  forwarded: "در حال پردازش",
  completed: "موفق",
  failed: "ناموفق",
  recovery_needed: "نیاز به بازیابی",
};

const inputClass =
  "h-9 px-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30";

export default function Logs() {
  const queryClient = useQueryClient();
  const [duration, setDuration] = useState<string>(loadDuration);
  const [modelInput, setModelInput] = useState("");
  const [model, setModel] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [status, setStatus] = useState("");
  const [apiKeyId, setApiKeyId] = useState("");
  const { data: filters } = useUsageFilters();
  const { data: keys } = useApiKeys();

  const filterOptions = filters as { endpoints: string[]; statuses: string[] } | undefined;

  // Persist the user's preferred duration
  useEffect(() => {
    localStorage.setItem(DURATION_STORAGE_KEY, duration);
  }, [duration]);

  // Debounce model search input
  useEffect(() => {
    const timer = setTimeout(() => setModel(modelInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [modelInput]);

  const isRefreshing = useIsFetching({ queryKey: ["usage"] }) > 0;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["usage"] });
  };

  const seriesFilters = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (model) p.model = model;
    if (endpoint) p.endpoint = endpoint;
    if (status) p.status_filter = status;
    if (apiKeyId) p.api_key_id = Number(apiKeyId);
    return p;
  }, [model, endpoint, status, apiKeyId]);

  const { data: series, isLoading: seriesLoading } = useUsageTimeseries(duration, seriesFilters);

  // Open-ended window: only bound the past. Omitting `to_time` lets each
  // refetch include logs created up to the current moment, so the refresh
  // button picks up brand-new requests instead of a window frozen at mount time.
  const timeRange = useMemo(() => {
    const now = new Date();
    return { from: new Date(now.getTime() - DURATION_SECONDS[duration] * 1000).toISOString() };
  }, [duration]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      from_time: timeRange.from,
    };
    if (model) p.model = model;
    if (endpoint) p.endpoint = endpoint;
    if (status) p.status_filter = status;
    if (apiKeyId) p.api_key_id = Number(apiKeyId);
    return p;
  }, [timeRange, model, endpoint, status, apiKeyId]);

  const {
    data,
    isLoading: logsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useUsageLogsInfinite(params);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Don't auto-load extra pages until the user actually scrolls.
  // Scrolling happens in the layout's <main> (overflow-y-auto), not the
  // window, so listen on document in the capture phase to catch nested scrollers.
  useEffect(() => {
    const onScroll = () => {
      hasScrolledRef.current = true;
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (hasScrolledRef.current && entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const logs = useMemo(
    () => (data?.pages ?? []).flat() as Record<string, unknown>[],
    [data]
  );

  const statusBadge = (s: string) => {
    switch (s) {
      case "completed": return <Badge variant="success">موفق</Badge>;
      case "failed": return <Badge variant="error">ناموفق</Badge>;
      case "forwarded": return <Badge variant="warning">در حال پردازش</Badge>;
      default: return <Badge variant="info">{STATUS_LABELS[s] || s}</Badge>;
    }
  };

  const columns = [
    {
      key: "created_at",
      header: "تاریخ",
      render: (item: Record<string, unknown>) => (
        <span className="text-small text-text-tertiary whitespace-nowrap">
          {new Date(item.created_at as string).toLocaleDateString("fa-IR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      ),
    },
    {
      key: "model",
      header: "مدل",
      render: (item: Record<string, unknown>) => (
        <span className="font-mono ltr text-left block">{item.model as string}</span>
      ),
    },
    {
      key: "provider",
      header: "تامین‌کننده",
      render: (item: Record<string, unknown>) => (
        <span className="text-body text-text-secondary">{item.provider as string || "—"}</span>
      ),
    },
    {
      key: "input_units",
      header: "ورودی",
      render: (item: Record<string, unknown>) => (
        <span className="font-number">{formatNumber(item.input_units as number)}</span>
      ),
    },
    {
      key: "output_units",
      header: "خروجی",
      render: (item: Record<string, unknown>) => (
        <span className="font-number">{formatNumber(item.output_units as number)}</span>
      ),
    },
    {
      key: "net_cost",
      header: "هزینه خالص",
      render: (item: Record<string, unknown>) => (
        <span className="font-number">
          {formatToman((Number(item.cost) || 0) + (Number(item.customer_caching_discount_irr) || 0))}
        </span>
      ),
    },
    {
      key: "customer_caching_discount_irr",
      header: "تخفیف",
      render: (item: Record<string, unknown>) => {
        const discount = Number(item.customer_caching_discount_irr) || 0;
        return discount > 0 ? (
          <span dir="ltr" className="font-number text-status-success-text">{formatToman(discount)}</span>
        ) : (
          <span className="font-number text-text-tertiary">—</span>
        );
      },
    },
    {
      key: "cost",
      header: "هزینه نهایی",
      render: (item: Record<string, unknown>) => (
        <span className="font-number font-medium">{formatToman(item.cost as number)}</span>
      ),
    },
    {
      key: "status",
      header: "وضعیت",
      render: (item: Record<string, unknown>) => statusBadge(item.status as string),
    },
    {
      key: "endpoint",
      header: "اندپوینت",
      render: (item: Record<string, unknown>) => (
        <span className="font-mono text-small text-text-tertiary ltr text-left block">{item.endpoint as string}</span>
      ),
    },
    {
      key: "api_key_name",
      header: "کلید API",
      render: (item: Record<string, unknown>) => (
        <span className="text-small text-text-secondary block" title={item.api_key_prefix as string || ""}>
          {item.api_key_name as string || item.api_key_prefix as string || "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header + Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 text-text-main">لاگ‌ها</h1>
          <p className="text-body text-text-secondary mt-1">گزارش دقیق درخواست‌ها و هزینه‌ها</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            title="بازه زمانی"
            className={inputClass}
          >
            {DURATIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <button
            onClick={refresh}
            title="به‌روزرسانی داده‌ها"
            className="inline-flex items-center justify-center w-9 h-9 rounded-btn border border-line bg-surface text-text-secondary hover:bg-surface-hover hover:text-text-main transition-colors"
          >
            <svg
              className={isRefreshing ? "animate-spin" : ""}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 11-2.64-6.36" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-small text-text-secondary mb-1.5">جستجوی مدل</label>
            <input
              type="text"
              value={modelInput}
              onChange={(e) => setModelInput(e.target.value)}
              placeholder="جستجو در نام مدل..."
              className={`${inputClass} w-full`}
            />
          </div>
          <div>
            <label className="block text-small text-text-secondary mb-1.5">فیلتر Endpoint</label>
            <select value={endpoint} onChange={(e) => setEndpoint(e.target.value)} className={`${inputClass} w-full ltr text-left`}>
              <option value="">همه</option>
              {(filterOptions?.endpoints || []).map((ep) => (
                <option key={ep} value={ep}>{ep}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-small text-text-secondary mb-1.5">فیلتر وضعیت</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputClass} w-full`}>
              <option value="">همه</option>
              {(filterOptions?.statuses || []).map((st) => (
                <option key={st} value={st}>{STATUS_LABELS[st] || st}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-small text-text-secondary mb-1.5">کلید API</label>
            <select value={apiKeyId} onChange={(e) => setApiKeyId(e.target.value)} className={`${inputClass} w-full`}>
              <option value="">همه</option>
              {(keys || []).map((k: Record<string, unknown>) => (
                <option key={k.id as number} value={k.id as number}>
                  {(k.name as string) || (k.key_prefix as string)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Token Usage Chart */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-h2 text-text-main">مصرف توکن</h2>
        </div>
        <TokenChart data={series} duration={duration} loading={seriesLoading} />
      </Card>

      {/* Logs Table */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-h2 text-text-main">گزارش مصرف</h2>
          <span className="text-small text-text-tertiary">{formatNumber(logs.length)} رکورد</span>
        </div>
        <DataTable
          columns={columns}
          data={logs}
          loading={logsLoading}
          emptyMessage="هیچ لاگی در این بازه یافت نشد"
        />
        <div ref={sentinelRef} className="flex items-center justify-center py-4">
          {isFetchingNextPage ? (
            <svg className="animate-spin h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : !hasNextPage && logs.length > 0 ? (
            <span className="text-small text-text-tertiary">پایان بازه انتخابی</span>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
